import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { InfoCircle16 } from '@honeybook/icons';
import HBTooltip from '../HBTooltip/HBTooltip';
import { vars } from '../../theme';
import { TargetContainer } from '../HBPopover/HBPopover.styles';
import { stopEventPropagation } from '../../utils/shared.utils';

export interface HBInfoIconProps {
    /** Content rendered inside the tooltip; when falsy, the entire component renders nothing. */
    tooltip: ReactNode;
    /**
     * Accessible name for the icon trigger, applied as `aria-label` on the
     * focusable target container. The trigger is a bare icon with no text of its
     * own, so without this it is announced with no name at all.
     *
     * Caveat: the container is a `div` with no ARIA role, and `aria-label` on a
     * roleless element is inconsistently announced across screen readers — this
     * improves the odds rather than guaranteeing the name. Tooltip content is
     * revealed on hover **and** on keyboard focus, so the content itself is
     * reachable either way. A properly announced, activatable trigger needs a
     * real `<button>`, which is tracked separately.
     */
    ariaLabel?: string;
    /** Custom icon component replacing the default InfoCircle16 icon. */
    icon?: typeof InfoCircle16;
    iconColor?: string;
    iconHoverColor?: string;
    /** Keeps the tooltip open while the cursor hovers over the tooltip content itself. */
    keepOpenOnHover?: boolean;
    /** Allows the tooltip to be open on initial render. */
    tooltipEnableInitialOpen?: boolean;
    /** Enables closing the tooltip by clicking outside of it. */
    tooltipShouldSupportClickOutside?: boolean;
    /** Callback fired when the tooltip opens, useful for analytics tracking. */
    onTooltipOpen?: () => void;
}

const HBInfoIcon = ({
    tooltip,
    ariaLabel,
    icon: InfoIcon = InfoCircle16,
    iconColor = vars.colors.text.tertiary,
    iconHoverColor = vars.colors.text.secondary,
    keepOpenOnHover = false,
    tooltipEnableInitialOpen,
    tooltipShouldSupportClickOutside,
    onTooltipOpen
}: HBInfoIconProps) => {
    if (!tooltip) {
        return null;
    }
    return (
        <Container
            $tooltipIconHoverColor={iconHoverColor}
            onClick={stopEventPropagation}
            onKeyPress={stopEventPropagation}
        >
            <HBTooltip
                placement="top"
                isTargetContainerFocusable
                targetAriaLabel={ariaLabel}
                target={<InfoIcon color={iconColor} />}
                keepOpenOnHover={keepOpenOnHover}
                enableInitialOpen={tooltipEnableInitialOpen}
                shouldSupportClickOutside={tooltipShouldSupportClickOutside}
                onOpenChange={
                    onTooltipOpen &&
                    (({ isOpen }) => {
                        if (isOpen) {
                            onTooltipOpen();
                        }
                    })
                }
            >
                {tooltip}
            </HBTooltip>
        </Container>
    );
};

export default HBInfoIcon;

const Container = styled.div<{
    $tooltipIconHoverColor: string;
}>`
    display: inline-flex;
    width: fit-content;
    height: fit-content;
    ${TargetContainer} {
        display: grid;
        i,
        &:before {
            display: flex;
            grid-area: 1 / 1;
        }

        i svg * {
            transition: fill 0.2s;
        }

        &:hover i svg * {
            fill: ${props => props.$tooltipIconHoverColor};
        }

        &:before {
            content: '';
            display: block;
            margin: -4px;
        }
    }
`;
